
"use client"

import { useState, useEffect } from "react"

export default function SectorLegalDashboard() {
  const [stats, setStats] = useState({
    tasaResolucion: 0,
    honorariosMedios: 0,
    demandaTiposMercantil: 0,
    tiempoMedioCasos: 0,
  })

  // Simulación de números que crecen estadísticamente
  useEffect(() => {
    const targetValues = {
      tasaResolucion: 68,
      honorariosMedios: 225,
      demandaTiposMercantil: 42,
      tiempoMedioCasos: 195,
    }

    const interval = setInterval(() => {
      setStats((prevStats) => ({
        tasaResolucion:
          prevStats.tasaResolucion < targetValues.tasaResolucion
            ? prevStats.tasaResolucion + Math.ceil((targetValues.tasaResolucion - prevStats.tasaResolucion) / 20)
            : targetValues.tasaResolucion,
        honorariosMedios:
          prevStats.honorariosMedios < targetValues.honorariosMedios
            ? prevStats.honorariosMedios + Math.ceil((targetValues.honorariosMedios - prevStats.honorariosMedios) / 20)
            : targetValues.honorariosMedios,
        demandaTiposMercantil:
          prevStats.demandaTiposMercantil < targetValues.demandaTiposMercantil
            ? prevStats.demandaTiposMercantil + Math.ceil((targetValues.demandaTiposMercantil - prevStats.demandaTiposMercantil) / 20)
            : targetValues.demandaTiposMercantil,
        tiempoMedioCasos:
          prevStats.tiempoMedioCasos < targetValues.tiempoMedioCasos
            ? prevStats.tiempoMedioCasos + Math.ceil((targetValues.tiempoMedioCasos - prevStats.tiempoMedioCasos) / 20)
            : targetValues.tiempoMedioCasos,
      }))
    }, 100)

    return () => clearInterval(interval)
  }, [])

  // Datos de ejemplo para las gráficas
  const generateChartData = (trend) => {
    const data = []
    let value = 30 + Math.random() * 10

    for (let i = 0; i < 12; i++) {
      if (trend === "up") {
        value += Math.random() * 5
      } else if (trend === "down") {
        value -= Math.random() * 3
      } else {
        value += (Math.random() - 0.5) * 8
      }
      value = Math.max(value, 10)
      data.push(value)
    }
    return data
  }

  const sectorTrends = [
    {
      title: "Digitalización del Sector",
      value: "78%",
      change: { value: "7", percentage: "7%", isPositive: true },
      chartData: generateChartData("up"),
    },
    {
      title: "Satisfacción Cliente",
      value: "82%",
      change: { value: "3", percentage: "3%", isPositive: true },
      chartData: generateChartData("up"),
    },
    {
      title: "Carga Procesal",
      value: "+5%",
      change: { value: "2", percentage: "2%", isPositive: false },
      chartData: generateChartData("up"),
    },
    {
      title: "Crecimiento del Sector",
      value: "3.2%",
      change: { value: "0.4", percentage: "0.4%", isPositive: true },
      chartData: generateChartData("fluctuate"),
    },
  ]

  // Implementación de MetricsCard
  const MetricsCard = ({ title, value, change, chart }) => (
    <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {chart}
      </div>
      <div className="flex items-end">
        <p className="text-2xl font-bold" style={{ color: "#1A365D" }}>
          {value}
        </p>
        {change && (
          <div className="flex items-center ml-2 pb-1">
            <div
              className={`flex items-center ${
                change.isPositive ? "text-green-500" : "text-red-500"
              } text-xs`}
            >
              <span className="mr-1">{change.isPositive ? "↑" : "↓"}</span>
              <span>{change.percentage}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  // Función para renderizar mini gráficas para las tarjetas
  const renderMiniChart = (data) => {
    const max = Math.max(...data)
    const min = Math.min(...data)
    const range = max - min

    const points = data
      .map((value, index) => {
        const x = index * (60 / (data.length - 1))
        const y = 20 - ((value - min) / range) * 20
        return `${x},${y}`
      })
      .join(" ")

    return (
      <svg width="60" height="20" className="ml-auto">
        <polyline points={points} fill="none" stroke="#D4AF37" strokeWidth="1.5" />
      </svg>
    )
  }

  // Componente de gráfico reemplazando StatsChart
  const SimpleStatsChart = () => {
    // Datos de ejemplo
    const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"]
    const mercantilData = [45, 52, 49, 60, 67, 62, 56, 59, 70, 72, 68, 74]
    const civilData = [35, 38, 40, 42, 45, 43, 47, 48, 52, 55, 58, 60]
    const laboralData = [25, 28, 30, 29, 32, 35, 38, 36, 39, 42, 45, 43]
    
    // Altura máxima del gráfico
    const maxValue = 100
    const height = 250
    const width = 700
    const padding = { top: 20, right: 30, bottom: 40, left: 40 }
    const chartWidth = width - padding.left - padding.right
    const chartHeight = height - padding.top - padding.bottom
    
    // Calcular posiciones
    const xStep = chartWidth / (monthNames.length - 1)
    
    const getY = (value) => {
      return chartHeight - (value / maxValue) * chartHeight + padding.top
    }
    
    // Generar líneas
    const generatePath = (data) => {
      return data.map((value, index) => {
        const x = index * xStep + padding.left
        const y = getY(value)
        return `${index === 0 ? "M" : "L"}${x},${y}`
      }).join(" ")
    }
    
    // Generación de leyenda
    const Legend = () => (
      <div className="flex justify-center mt-4 space-x-6">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
          <span className="text-sm text-gray-600">Mercantil</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
          <span className="text-sm text-gray-600">Civil</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
          <span className="text-sm text-gray-600">Laboral</span>
        </div>
      </div>
    )
    
    return (
      <div>
        <svg width={width} height={height} className="mx-auto">
          {/* Líneas de referencia horizontales */}
          {[0, 25, 50, 75, 100].map((value) => (
            <g key={value}>
              <line
                x1={padding.left}
                y1={getY(value)}
                x2={width - padding.right}
                y2={getY(value)}
                stroke="#e5e5e5"
                strokeDasharray="4,4"
              />
              <text
                x={padding.left - 10}
                y={getY(value)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="12"
                fill="#666"
              >
                {value}
              </text>
            </g>
          ))}
          
          {/* Etiquetas del eje X */}
          {monthNames.map((month, index) => (
            <text
              key={month}
              x={index * xStep + padding.left}
              y={height - padding.bottom / 2}
              textAnchor="middle"
              fontSize="12"
              fill="#666"
            >
              {month}
            </text>
          ))}
          
          {/* Líneas de datos */}
          <path d={generatePath(mercantilData)} fill="none" stroke="#3182ce" strokeWidth="2" />
          <path d={generatePath(civilData)} fill="none" stroke="#48bb78" strokeWidth="2" />
          <path d={generatePath(laboralData)} fill="none" stroke="#ecc94b" strokeWidth="2" />
          
          {/* Puntos para cada mes */}
          {mercantilData.map((value, index) => (
            <circle
              key={`mercantil-${index}`}
              cx={index * xStep + padding.left}
              cy={getY(value)}
              r="4"
              fill="#3182ce"
            />
          ))}
          
          {civilData.map((value, index) => (
            <circle
              key={`civil-${index}`}
              cx={index * xStep + padding.left}
              cy={getY(value)}
              r="4"
              fill="#48bb78"
            />
          ))}
          
          {laboralData.map((value, index) => (
            <circle
              key={`laboral-${index}`}
              cx={index * xStep + padding.left}
              cy={getY(value)}
              r="4"
              fill="#ecc94b"
            />
          ))}
        </svg>
        <Legend />
      </div>
    )
  }

  // Icono simple
  const SimpleIcon = ({ type }) => {
    let path = ""
    
    switch(type) {
      case "chart":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        )
      case "globe":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
        )
      case "pie":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
            <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
          </svg>
        )
      case "trending":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
            <polyline points="17 6 23 6 23 12"></polyline>
          </svg>
        )
      default:
        return null
    }
  }

  // Datos para el gráfico de distribución por áreas
  const renderDistributionChart = () => {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-center">
          <div className="h-32 w-32 mx-auto text-blue-900 opacity-30">
            <SimpleIcon type="pie" />
          </div>
          <p className="mt-4 text-gray-500">Gráfico de distribución por áreas legales</p>
        </div>
      </div>
    )
  }

  // Datos para el mapa de calor jurisdiccional
  const renderHeatMap = () => {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-center">
          <div className="h-32 w-32 mx-auto text-blue-900 opacity-30">
            <SimpleIcon type="globe" />
          </div>
          <p className="mt-4 text-gray-500">Mapa de actividad jurisdiccional</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold text-navy-900">Panorama del Sector Legal</h1>

      {/* Estadísticas generales del sector */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Indicadores Clave del Sector
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricsCard
            title="Tasa Resolución Judicial"
            value={`${stats.tasaResolucion}%`}
            change={{
              value: "4%",
              percentage: "4%",
              isPositive: true,
            }}
            chart={renderMiniChart(generateChartData("up"))}
          />

          <MetricsCard
            title="Honorarios Medios (€/h)"
            value={`${stats.honorariosMedios}`}
            change={{
              value: "15",
              percentage: "7%",
              isPositive: true,
            }}
            chart={renderMiniChart(generateChartData("up"))}
          />

          <MetricsCard
            title="Demanda Derecho Mercantil"
            value={`+${stats.demandaTiposMercantil}%`}
            change={{
              value: "6%",
              percentage: "6%",
              isPositive: true,
            }}
            chart={renderMiniChart(generateChartData("up"))}
          />

          <MetricsCard
            title="Tiempo Medio Resolución (días)"
            value={stats.tiempoMedioCasos.toString()}
            change={{
              value: "-12",
              percentage: "6%",
              isPositive: true,
            }}
            chart={renderMiniChart(generateChartData("down"))}
          />
        </div>
      </div>

      {/* Gráfica de tendencias anuales */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Evolución de Casos por Especialidad
        </h2>
        <div className="overflow-x-auto">
          <SimpleStatsChart />
        </div>
      </div>

      {/* Tendencias del sector */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Tendencias del Sector Legal
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {sectorTrends.map((card, index) => (
            <div key={index} className="cursor-pointer">
              <MetricsCard
                title={card.title}
                value={card.value}
                change={card.change}
                chart={renderMiniChart(card.chartData)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Distribución por áreas y mapa jurisdiccional */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
            Distribución por Áreas de Práctica
          </h2>
          {renderDistributionChart()}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
            Actividad Jurisdiccional por Región
          </h2>
          {renderHeatMap()}
        </div>
      </div>

      {/* Datos del mercado laboral */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "#1A365D" }}>
          Mercado Laboral del Sector Legal
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Demanda de Profesionales</h3>
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-blue-900">+8%</span>
              <div className="text-green-500">
                <SimpleIcon type="trending" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">Crecimiento anual</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Salario Medio</h3>
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-blue-900">54.200€</span>
              <div className="text-green-500">
                <SimpleIcon type="trending" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">+3.5% vs año anterior</p>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Nuevas Contrataciones</h3>
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-blue-900">+5.2%</span>
              <div className="text-green-500">
                <SimpleIcon type="trending" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">Variación trimestral</p>
          </div>
        </div>
      </div>
    </div>
  )
}